import { useCreativesConfig } from '@ad-lucy/context/creativesConfig';
import { EVENT, EventsUtils } from '@ad-lucy/utils';
import type EventEmitter from 'events';
import { createContext, type ReactElement, useContext, useEffect, useMemo, useState } from 'react';

import type { AdStateContextType, AdStateProviderProps } from './types';

export const AdStateContext = createContext<AdStateContextType>({} as AdStateContextType);
export const useAdState = (): AdStateContextType => useContext(AdStateContext);

const events = (): EventEmitter => EventsUtils.AppEventEmitter.Instance;

export function AdStateProvider({ children }: Readonly<AdStateProviderProps>): ReactElement {
  const {
    settings: { autoplay: defaultAutoplay },
  } = useCreativesConfig();

  const [playing, setPlaying] = useState<boolean>(true);
  const [autoplay, setAutoplay] = useState<boolean>(defaultAutoplay);
  const [muted, setMuted] = useState<boolean>(true);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [hasFiredInitialInteraction, setHasFiredInitialInteraction] = useState(false);
  const [hasOpenedContinueOverlay, setHasOpenedContinueOverlay] = useState(false);
  const [closeAd, setCloseAd] = useState<boolean>(false);
  const [adCompleted, setAdCompleted] = useState<boolean>(false);

  const onPause = (): void => {
    setPlaying(false);
  };

  const onPlay = (): void => {
    setPlaying(true);
  };

  const onMute = (shouldMute: boolean): void => {
    setMuted(shouldMute);
  };

  const onHasFiredInitialInteraction = (): void => {
    setHasFiredInitialInteraction(true);
  };

  useEffect(() => {
    events().on(EVENT.PAUSE, onPause);
    events().on(EVENT.PLAY, onPlay);
    events().on(EVENT.MUTE, onMute);
    events().once(EVENT.INITIAL_INTERACTION, onHasFiredInitialInteraction);

    return () => {
      events().off(EVENT.PAUSE, onPause);
      events().off(EVENT.PLAY, onPlay);
      events().off(EVENT.MUTE, onMute);
      events().off(EVENT.INITIAL_INTERACTION, onHasFiredInitialInteraction);
    };
  });

  const adStateContextValue: AdStateContextType = useMemo(
    () => ({
      playing,
      muted,
      showMenu,
      autoplay,
      setCloseAd,
      setAutoplay,
      setShowMenu,
      hasFiredInitialInteraction,
      hasOpenedContinueOverlay,
      setHasOpenedContinueOverlay,
      adCompleted,
      setAdCompleted,
    }),
    [
      playing,
      muted,
      showMenu,
      autoplay,
      hasFiredInitialInteraction,
      hasOpenedContinueOverlay,
      adCompleted,
    ],
  );

  return (
    <AdStateContext.Provider value={adStateContextValue}>
      {closeAd ? null : children}
    </AdStateContext.Provider>
  );
}
